/** Duplicate finder — v1 cFindDupes() with MERGING through the Phase 11
 * door: same union-find groups (shared phone / email / name+company), same
 * "most complete" default keeper (v1 _cScore), same merge semantics
 * (v1 _cMergeInto — see api.mergeContactsRaw). */
import { useMemo, useState } from "react";
import { contactScore, dupGroups, type Contact } from "./api";

export function DupesPanel({
  rows,
  onClose,
  onOpen,
  onMerge,
}: {
  rows: Contact[];
  onClose: () => void;
  onOpen: (c: Contact) => void;
  /** merge one group: (keeper, losers). null = write door unavailable */
  onMerge: ((keeper: Contact, losers: Contact[]) => void) | null;
}) {
  const groups = useMemo(() => dupGroups(rows), [rows]);
  // chosen keeper per group index (default: most complete)
  const [keep, setKeep] = useState<Record<number, number>>({});
  const [confirmAll, setConfirmAll] = useState(false);

  const keeperOf = (g: number[], gi: number): number => {
    const chosen = keep[gi];
    if (chosen !== undefined && g.includes(chosen)) return chosen;
    return [...g].sort((a, b) => contactScore(rows[b]!) - contactScore(rows[a]!))[0]!;
  };

  const mergeOne = (gi: number) => {
    const g = groups[gi];
    if (!g || !onMerge) return;
    const kIdx = keeperOf(g, gi);
    onMerge(rows[kIdx]!, g.filter((i) => i !== kIdx).map((i) => rows[i]!));
  };

  const mergeAll = () => {
    if (!onMerge) return;
    for (let gi = 0; gi < groups.length; gi++) {
      const g = groups[gi]!;
      const kIdx = keeperOf(g, gi);
      onMerge(rows[kIdx]!, g.filter((i) => i !== kIdx).map((i) => rows[i]!));
    }
    setConfirmAll(false);
    onClose();
  };

  return (
    <div className="calmodalwrap" onClick={onClose}>
      <div className="calmodal wide" onClick={(e) => e.stopPropagation()}>
        <header className="calmodalhead">
          <strong>
            Duplicate contacts{" "}
            {groups.length ? <span className="dupcount">{groups.length} group{groups.length > 1 ? "s" : ""}</span> : null}
          </strong>
          <button className="close" onClick={onClose}>×</button>
        </header>
        <div className="calmodalbody">
          {groups.length === 0 ? (
            <div className="empty">
              <strong>No duplicates found</strong>
              <span>Every contact has a unique phone, email and name ✓</span>
            </div>
          ) : (
            <>
              <p className="sub dupintro">
                Pick which record to KEEP in each group — its blank fields are filled from the
                others, extra phones/emails are kept (work/home/secondary), tags combine, and
                the discarded copies are removed for good (they can't be re-imported).
              </p>
              {groups.map((g, gi) => {
                const kIdx = keeperOf(g, gi);
                return (
                  <div key={gi} className="dupgroup">
                    {g.map((ci) => {
                      const c = rows[ci]!;
                      const line = [
                        c.company, c.phone, c.email,
                        c.sourceCode ? "src: " + c.sourceCode : null,
                        c.tags.length ? "tags: " + c.tags.join(", ") : null,
                      ].filter(Boolean).join(" · ");
                      return (
                        <div key={c.natKey} className={"duprow pick" + (ci === kIdx ? " best" : "")}>
                          {onMerge ? (
                            <input
                              type="radio"
                              name={"dup-" + gi}
                              checked={ci === kIdx}
                              onChange={() => setKeep((p) => ({ ...p, [gi]: ci }))}
                              title="Keep this record"
                            />
                          ) : null}
                          <button className="duprowbody" onClick={() => onOpen(c)}>
                            <strong>
                              {c.name || "(no name)"}
                              {ci === kIdx ? <span className="dupbest">keep</span> : null}
                            </strong>
                            <span className="sub">{line || "—"}</span>
                          </button>
                        </div>
                      );
                    })}
                    {onMerge ? (
                      <div className="dupactions">
                        <button className="btn slim warnbtn" onClick={() => mergeOne(gi)}>
                          Merge — keep selected
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {onMerge && groups.length > 1 ? (
                confirmAll ? (
                  <div className="dupactions all">
                    <span className="sub">Merge ALL {groups.length} groups, keeping the most complete of each?</span>
                    <button className="btn slim warnbtn" onClick={mergeAll}>Yes, merge all</button>
                    <button className="vtab" onClick={() => setConfirmAll(false)}>Cancel</button>
                  </div>
                ) : (
                  <div className="dupactions all">
                    <button className="btn slim warnbtn" onClick={() => setConfirmAll(true)}>
                      Merge ALL groups (keep most complete)
                    </button>
                  </div>
                )
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
